import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { Button } from 'antd';
import './Navbar.css';
import {
  logout,
  isLoggedIn,
  getUser,
  getToken,
  getRefreshToken,
  setSession,
  getUserRole,
} from '../../utils/auth';
import { UserProfileService } from '../../services/UserProfileService';
import { apiUrls } from '../../services/ApiUrls';
import { Avatar, Dropdown, Menu, Progress } from 'antd';
import {
  parseProfileCompletionPercent,
  STUDENT_PROFILE_COMPLETION_ALERT_THRESHOLD,
} from '../../utils/studentProfileLoginFlow';
import { DownOutlined, MenuOutlined, CloseOutlined, InboxOutlined, CrownOutlined } from '@ant-design/icons';
import MenuLogin from '../common/MenuLogin';
import MenuRegistration from '../common/MenuRegistration';
import NotificationDropdown from '../notifications/NotificationDropdown';
import { ROUTES } from '../../config/routes';

/** Blue circle + white outline figure when user has no profile photo (file in `public/images/`). */
const DEFAULT_NAV_PLACEHOLDER_SRC = '/images/default-profile-placeholder.svg';

const getDashboardPath = (role?: string): string => {
  switch (role) {
    case 'ORGANIZATION':
      return ROUTES.ORGANIZATION.DASHBOARD;
    case 'MENTOR':
      return ROUTES.MENTOR.DASHBOARD;
    case 'SPONSOR':
      return ROUTES.SPONSOR.DASHBOARD;
    default:
      return ROUTES.STUDENT.DASHBOARD;
  }
};

/** Portal routes (dashboard, jobs, internships, etc.) highlight "My Dashboard" in the nav. */
const isDashboardNavActive = (role: string | undefined, pathname: string): boolean => {
  switch (role) {
    case 'ORGANIZATION':
      return pathname.startsWith('/organization/');
    case 'STUDENT':
      return pathname.startsWith('/student/') || pathname.startsWith('/profile/');
    case 'MENTOR':
      return pathname.startsWith('/mentor/') || pathname.startsWith('/mentorship/');
    case 'SPONSOR':
      return pathname.startsWith('/sponsor/');
    default:
      return false;
  }
};

const Navbar: React.FC = () => {
  const [loggedIn, setLoggedIn] = useState(isLoggedIn());
  const [user, setUser] = useState(getUser());
  const [headerProfilePicUrl, setHeaderProfilePicUrl] = useState<string | undefined>(undefined);
  const [headerAvatarError, setHeaderAvatarError] = useState(false);
  const headerObjectUrlRef = useRef<string | undefined>(undefined);
  const navigate = useNavigate();
  const location = useLocation();
  const userRole = getUserRole();

  useEffect(() => {
    // Listen for login/logout in other tabs
    const handleStorage = () => {
      setLoggedIn(isLoggedIn());
      setUser(getUser());
      setHeaderProfilePicUrl(undefined);
      setHeaderAvatarError(false);
    };
    window.addEventListener('storage', handleStorage);

    // Listen for login/logout in the same tab
    const handleAuthChange = () => {
      setLoggedIn(isLoggedIn());
      setUser(getUser());
      setHeaderProfilePicUrl(undefined);
      setHeaderAvatarError(false);
    };
    window.addEventListener('authChange', handleAuthChange);

    return () => {
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('authChange', handleAuthChange);
    };
  }, []);

  /** Load profile photo via authenticated content endpoint (reliable for private S3 objects). */
  useEffect(() => {
    if (!loggedIn || !user?.email) return;
    const rolesWithPersonalProfile = ['STUDENT', 'MENTOR', 'SPONSOR', 'ORGANIZATION'];
    if (!userRole || !rolesWithPersonalProfile.includes(userRole)) {
      return;
    }

    let cancelled = false;

    const revokeHeaderObjectUrl = () => {
      if (headerObjectUrlRef.current) {
        URL.revokeObjectURL(headerObjectUrlRef.current);
        headerObjectUrlRef.current = undefined;
      }
    };

    const loadAvatar = async () => {
      const token = getToken();
      if (!token) return;
      revokeHeaderObjectUrl();
      setHeaderAvatarError(false);
      setHeaderProfilePicUrl(undefined);

      try {
        const res = await fetch(apiUrls.userProfile.personalInfo.profilePictureContent, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        if (res.ok) {
          const blob = await res.blob();
          if (blob.size > 0) {
            const objectUrl = URL.createObjectURL(blob);
            headerObjectUrlRef.current = objectUrl;
            setHeaderProfilePicUrl(objectUrl);
            return;
          }
        }
      } catch {
        /* fall through to presigned URL */
      }

      try {
        const info = await UserProfileService.getPersonalInfo();
        if (cancelled || info.status !== 'success' || !info.data?.profilePicUrl) return;
        const url = info.data.profilePicUrl.trim();
        if (!url) return;
        setHeaderProfilePicUrl(url);
        const cur = getUser();
        if (cur?.email === user.email) {
          const sessionToken = getToken();
          if (sessionToken) {
            setSession(sessionToken, { ...cur, profilePicUrl: url }, getRefreshToken() || undefined);
          }
        }
      } catch {
        /* ignore */
      }
    };

    void loadAvatar();

    return () => {
      cancelled = true;
      revokeHeaderObjectUrl();
    };
  }, [loggedIn, user?.email, user?.profilePicUrl, userRole]);

  const profileCompletionPercent =
    userRole === 'STUDENT' ? parseProfileCompletionPercent(user) : null;

  const profileCompletionColor =
    profileCompletionPercent != null &&
    profileCompletionPercent >= STUDENT_PROFILE_COMPLETION_ALERT_THRESHOLD
      ? '#52c41a'
      : '#faad14';

  const showProfileCompletionWarning =
    profileCompletionPercent != null &&
    profileCompletionPercent < STUDENT_PROFILE_COMPLETION_ALERT_THRESHOLD;

  const headerAvatarSrc =
    headerAvatarError || !headerProfilePicUrl?.trim()
      ? DEFAULT_NAV_PLACEHOLDER_SRC
      : headerProfilePicUrl.trim();

  const menu = (
    <Menu>
      <Menu.Item key="profile" onClick={() => {
        if (userRole === 'ORGANIZATION') {
          navigate(ROUTES.ORGANIZATION.PROFILE);
        } else if (userRole === 'MENTOR') {
          navigate(ROUTES.MENTOR.PROFILE);
        } else if (userRole === 'SPONSOR') {
          navigate(ROUTES.SPONSOR.PROFILE);
        } else {
          navigate(ROUTES.STUDENT.PROFILE);
        }
      }}>
        My Profile
      </Menu.Item>
      <Menu.Item key="inbox" icon={<InboxOutlined />} onClick={() => navigate('/inbox')}>
        Inbox
      </Menu.Item>
      {userRole === 'ORGANIZATION' && (
        <Menu.Item key="subscription" icon={<CrownOutlined />} onClick={() => navigate(ROUTES.ORGANIZATION.SUBSCRIPTION)}>
          Subscription
        </Menu.Item>
      )}
      <Menu.Item key="logout" onClick={logout}>
        Logout
      </Menu.Item>
    </Menu>
  );

  // State for login/registration modals and mobile menu
  const [loginVisible, setLoginVisible] = React.useState(false);
  const [registerVisible, setRegisterVisible] = React.useState(false);
  const [loginDefaultTab, setLoginDefaultTab] = React.useState<'student' | 'employer' | 'mentor'>('student');
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Handlers to switch between login and registration
  const handleShowLogin = (tab: 'student' | 'employer' | 'mentor' = 'student') => {
    setRegisterVisible(false);
    setLoginDefaultTab(tab);
    setLoginVisible(true);
  };
  const handleShowRegister = () => {
    setLoginVisible(false);
    setRegisterVisible(true);
  };

  return (
    <nav className="navbar">
      <div className="navbar-container">
        {/* Logo/Brand */}
        <Link to="/">
          <img src="/logo.svg" alt="enpropeL" />
        </Link>


        {/* Center Navigation */}
        <div className={`navbar-center ${mobileMenuOpen ? 'mobile-open' : ''}`}>
          <ul className="nav-links">
            {loggedIn && userRole && (userRole === 'ORGANIZATION' || userRole === 'STUDENT' || userRole === 'MENTOR' || userRole === 'SPONSOR') && (
              <li>
                <NavLink
                  to={getDashboardPath(userRole)}
                  className={`nav-link ${isDashboardNavActive(userRole, location.pathname) ? 'active' : ''}`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  My Dashboard
                </NavLink>
              </li>
            )}
            <li>
              <NavLink to="/home" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                Home
              </NavLink>
            </li>
            <li>
              <NavLink to="/internships" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                Internship
              </NavLink>
            </li>
            <li>
              <NavLink to={ROUTES.PUBLIC.JOBS} className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                Jobs
              </NavLink>
            </li>
            <li>
              <NavLink to="/mentorships" className="nav-link" onClick={() => setMobileMenuOpen(false)}>
                Mentorship
              </NavLink>
            </li>
            <li className="nav-dropdown">
              <button
                type="button"
                className={`nav-link nav-dropdown-trigger ${location.pathname.startsWith(ROUTES.PUBLIC.NEWS_MEDIA) ? 'active' : ''}`}
              >
                More <DownOutlined />

              </button>
              <div className="nav-mega-menu">
                <div className="nav-mega-menu__column">
                  <NavLink to={ROUTES.PUBLIC.NEWS_MEDIA} className="nav-mega-menu__item" onClick={() => setMobileMenuOpen(false)}>

                    News and Media
                  </NavLink>
                  <NavLink to={ROUTES.PUBLIC.SCHOLARSHIPS} className="nav-mega-menu__item" onClick={() => setMobileMenuOpen(false)}>
                    Scholarship
                  </NavLink>
                  <NavLink to={ROUTES.PUBLIC.PROGRAMS} className="nav-mega-menu__item" onClick={() => setMobileMenuOpen(false)}>
                    Programs
                  </NavLink>
                </div>

              </div>
            </li>

          </ul>
        </div>

        {/* Right side buttons */}
        <div className="navbar-actions">
          <Button
            className="mobile-menu-toggle"
            type="text"
            icon={mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          />
          {loggedIn ? (
            <>
              <NotificationDropdown />
              <Dropdown overlay={menu} placement="bottomRight" trigger={["click"]}>
                <div className="navbar-user-menu">
                  <div className="navbar-user-text">
                    <span className="user-name">
                      {user?.fullname || user?.email || user?.userUniqueId || 'My account'}
                    </span>
                    {profileCompletionPercent != null && (
                      <div className="navbar-profile-completion" title="Profile completion">
                        <Progress
                          percent={profileCompletionPercent}
                          showInfo={false}
                          size="small"
                          strokeColor={profileCompletionColor}
                          trailColor="rgba(255, 255, 255, 0.2)"
                          className="navbar-profile-completion-bar"
                        />
                        <span
                          className="navbar-profile-completion-label"
                          style={{ color: profileCompletionColor }}
                        >
                          Profile {profileCompletionPercent}% complete
                        </span>
                      </div>
                    )}
                  </div>
                  <Avatar
                    key={headerAvatarSrc}
                    size="large"
                    className="navbar-user-avatar"
                    style={{ verticalAlign: 'middle', flexShrink: 0 }}
                    src={headerAvatarSrc}
                    onError={() => {
                      setHeaderAvatarError(true);
                      setHeaderProfilePicUrl(undefined);
                      return false;
                    }}
                  />
                </div>
              </Dropdown>
            </>
          ) : (
            <>
              <Button type="text" className="signin-btn" onClick={() => handleShowLogin('student')}>
                Sign in
              </Button>
              <Button type="primary" className="create-account-btn" onClick={handleShowRegister}>
                Create Account
              </Button>
              <MenuLogin
                visible={loginVisible}
                loading={false}
                onCancel={() => {
                  setLoginVisible(false);
                  localStorage.removeItem('activeTab');
                }}
                onRegister={handleShowRegister}
                defaultTab={loginDefaultTab}
              />
              <MenuRegistration
                visible={registerVisible}
                loading={false}
                onCancel={() => {
                  setRegisterVisible(false);
                  localStorage.removeItem('activeTab');
                }}
                onLogin={() => handleShowLogin('student')}
              />
            </>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 